import { idb } from "../../db/index.ts";
import { g, logEvent } from "../../util/index.ts";
import { team } from "../index.ts";
import { apronsActive, apronLevel, APRON_CONFIG } from "./aprons.ts";

// Second-apron frozen picks. While a team is over the 2nd apron, its furthest-out OWN first-round
// pick is frozen (untradeable) — you can't mortgage the future while you're already all-in over
// the apron. Re-evaluated each offseason from current payroll, so the pick thaws once the team
// drops back under the apron.
//
// The NBA freezes the pick 7 years out (freezePickOffset); real-players leagues already carry 7
// seasons of future picks so it exists, while random leagues carry fewer, so we clamp to the
// furthest available own first-rounder. `payrolls` can be injected (else fetched).
// Drop-to-end-of-round (a repeat-offender pick sliding to #30) is deferred.
const freezeApronPicks = async (payrolls?: Record<number, number>) => {
	if (!apronsActive()) {
		return;
	}

	const offset = Math.min(
		APRON_CONFIG.freezePickOffset,
		g.get("numSeasonsFutureDraftPicks"),
	);
	const targetSeason = g.get("season") + offset;

	const payrollsByTid = payrolls ?? (await team.getPayrolls());
	const draftPicks = await idb.cache.draftPicks.getAll();

	for (let tid = 0; tid < g.get("numTeams"); tid++) {
		const payroll = payrollsByTid[tid];
		if (payroll === undefined) {
			continue;
		}
		const shouldFreeze = apronLevel(payroll) === 3;

		// Control lookup: freeze only a pick this team both ORIGINATED (originalTid) and still
		// CONTROLS (tid) — you can't freeze a pick you've already traded to someone else, and
		// another team's pick you acquired isn't frozen by YOUR apron status.
		const pick = draftPicks.find(
			(dp) =>
				dp.tid === tid &&
				dp.originalTid === tid &&
				dp.round === 1 &&
				dp.season === targetSeason,
		);
		if (!pick) {
			continue;
		}

		const wasFrozen = !!pick.frozen;
		if (shouldFreeze === wasFrozen) {
			continue;
		}

		if (shouldFreeze) {
			pick.frozen = true;
		} else {
			delete pick.frozen;
		}
		await idb.cache.draftPicks.put(pick);

		// Tell the user when their own pick freezes or thaws.
		if (tid === g.get("userTid")) {
			logEvent({
				type: "draft",
				text: shouldFreeze
					? `Your ${targetSeason} first-round pick is frozen (untradeable) because you're over the second apron.`
					: `Your ${targetSeason} first-round pick is no longer frozen — you're back under the second apron.`,
				tids: [tid],
				showNotification: true,
				score: 20,
				saveToDb: false,
			});
		}
	}
};

export default freezeApronPicks;
